<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'dunning-letters',
    path: '/dunning-letters',
    component: 'DunningLetters',
    title: () => pgettext('DunningLetters', 'Dunning Letters'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'DunningLetters',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'reference_number',
      name: $fn.value.pgettext('DunningLetters', 'Reference Number'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'dunningLetter',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'status',
      name: $fn.value.pgettext('DunningLetters', 'Status'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'DunningLetterStatus',
            conversion: 'StartCase',
          },
          {
            name: 'badge',
            variantMap: {
              sent: 'light',
              delivered: 'success',
              error: 'danger',
            },
            variantMapField: 'status',
          },
        ],
      },
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('DunningLetters', 'Owner company'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'customer_name',
      name: $fn.value.pgettext('DunningLetters', 'Partner'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'customer_email',
      name: $fn.value.pgettext('DunningLetters', 'Client Email'),
      formatter: {
        name: 'join',
        separator: '; ',
      },
    },
    {
      field: 'severity',
      name: $fn.value.pgettext('DunningLetters', 'Severity'),
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('DunningLetters', 'Created at'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'created_by',
      name: $fn.value.pgettext('DunningLetters', 'Created by'),
      formatter: {
        name: 'user',
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('integro_api/list_dunning_letter');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('DunningLetters', 'Dunning Letters'),
      icon: $config.value.dunningLetter.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_uwayfdt7x_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_di3yq39ny_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_showModal_pws1a5mj0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'SendMails',
      props: {
        items: selectedItems.value,
      },
    };
  };
  const act_2095089031469157_script_3t4ltgl6g_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_2095089031469157_script_3t4ltgl6g_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2095089031469157_script_3t4ltgl6g_value_callable($event);
      },
    };
  };
  const act_2095089031469157_request_e762g93m7_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_delete_dunning_letter',
      data: selectedItems.value.map((p) => ({ id: p.id, etag: null })),
      parameters: { id: selectedItems.value.map((p) => p.id) },
    };
  };

  const act_7315092382398562_closeModal_uwayfdt7x =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_uwayfdt7x_getActionArgs,
    });
  const inst_evh_7315092382398562_close_wuw20oary =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_uwayfdt7x,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_wuw20oary],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_4h7i8b9lz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_7bnj70hit =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_4h7i8b9lz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_7bnj70hit],
    event: 'reload',
  });
  const act_2248226175642056_setData_di3yq39ny = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_di3yq39ny_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_oiery5l7o =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_di3yq39ny,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_oiery5l7o],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_showModal_pws1a5mj0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_pws1a5mj0_getActionArgs,
    });
  const inst_evh_4615685607825208_click_s8b6kpgzp =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_pws1a5mj0,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_s8b6kpgzp],
    event: 'click',
  });
  const act_2095089031469157_script_3t4ltgl6g = new actionTypes.ScriptAction({
    actionArgs: act_2095089031469157_script_3t4ltgl6g_getActionArgs,
  });
  const inst_evh_2095089031469157_success_mkmdwfv99 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_script_3t4ltgl6g,
      event: 'success',
      displayName: 'script',
    });
  const act_2095089031469157_request_e762g93m7 = new actionTypes.RequestAction({
    actionArgs: act_2095089031469157_request_e762g93m7_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_2095089031469157_success_mkmdwfv99],
  });
  const inst_evh_2095089031469157_click_kqwpjz538 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_request_e762g93m7,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_kqwpjz538],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_wuw20oary,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_7bnj70hit,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_oiery5l7o,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_click_s8b6kpgzp,
    inst_evh_4615685607825208_click,
    inst_evh_2095089031469157_success_mkmdwfv99,
    inst_evh_2095089031469157_click_kqwpjz538,
    deleteItems: inst_evh_2095089031469157_click_kqwpjz538,
    inst_evh_2095089031469157_click,
  };

  const evh_7315092382398562_close_wuw20oary = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_wuw20oary.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_7bnj70hit = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_7bnj70hit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_oiery5l7o = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_oiery5l7o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_s8b6kpgzp = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_s8b6kpgzp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_mkmdwfv99 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_mkmdwfv99.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_kqwpjz538 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_kqwpjz538.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="DunningLetters"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="dunning-letters"
      :headerTitle="pgettext('DunningLetters', 'Dunning letters')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'dunning-letters')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            icon="fa-envelope"
            :text="pgettext('DunningLetters', 'Send emails...')"
            size="sm"
            variant="primary"
            :disabled="!selectedItems || !selectedItems.length"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <ItpButton
            icon="fa-trash"
            :text="pgettext('DunningLetters', 'Delete')"
            size="sm"
            variant="danger"
            :disabled="!selectedItems || !selectedItems.length"
            :spinning="actions.deleteItems.isRunning"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/claims/dunning-letters.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
